import "./cart.modern.css"
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaShoppingCart, FaTrash, FaShieldAlt, FaTruck, FaUndo } from 'react-icons/fa';

function ModernCart() {
    const navigate = useNavigate()
    const [cartItems, setCartItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)

    useEffect(() => {
        loadCart();
    }, [])

    async function loadCart() {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            console.log("✅ Cart loaded:", cart);
            setCartItems(cart);
        } catch (error) {
            console.error('❌ Error loading cart:', error);
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    }

    function showToast(message) {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    }

    async function changeQuantity(sku, newQuantity) {
        if (newQuantity < 1) return;
        
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            const updatedCart = cart.map(item => 
                item.sku === sku ? { ...item, quantity: newQuantity } : item
            );
            localStorage.setItem('cart', JSON.stringify(updatedCart));
            window.dispatchEvent(new Event('cartUpdated'));
            await loadCart();
            showToast('Cart updated');
        } catch (error) {
            console.error('Error updating quantity:', error);
        }
    }

    async function deleteItem(sku) {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            const updatedCart = cart.filter(item => item.sku !== sku);
            localStorage.setItem('cart', JSON.stringify(updatedCart));
            window.dispatchEvent(new Event('cartUpdated'));
            await loadCart();
            showToast('Item removed from cart');
        } catch (error) {
            console.error('Error removing item:', error);
        }
    }

    const subtotal = cartItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const shipping = 0; // Free shipping
    const total = subtotal + shipping;

    // Free shipping progress (threshold: $75)
    const freeShippingThreshold = 75;
    const shippingProgress = Math.min((subtotal / freeShippingThreshold) * 100, 100);
    const amountToFreeShipping = Math.max(freeShippingThreshold - subtotal, 0);

    if (loading) {
        return <div className="cart-loading">Loading cart...</div>;
    }

    // Empty Cart State
    if (cartItems.length === 0) {
        return (
            <div className="modern-cart-page">
                <div className="empty-cart-modern">
                    <div className="empty-cart-card">
                        <div className="empty-cart-icon">
                            <FaShoppingCart />
                        </div>
                        <h1 className="empty-cart-title">Your cart is empty</h1>
                        <p className="empty-cart-subtitle">
                            Browse best sellers or explore categories to get started.
                        </p>
                        <div className="empty-cart-actions">
                            <button 
                                className="empty-cart-primary-btn"
                                onClick={() => navigate('/best-sellers')}
                            >
                                Shop Best Sellers
                            </button>
                            <button 
                                className="empty-cart-secondary-btn"
                                onClick={() => navigate('/products')}
                            >
                                Browse Categories
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Filled Cart State
    return (
        <div className="modern-cart-page">
            <h1 className="cart-page-title">Shopping Cart</h1>

            <div className="cart-layout">
                {/* Cart Items */}
                <div className="cart-items-section">
                    {cartItems.map((item) => (
                        <div key={item.sku} className="cart-item-card">
                            <div className="cart-item-image">
                                <img src={item.image_url} alt={item.name} />
                            </div>

                            <div className="cart-item-details">
                                <h3 className="cart-item-name">{item.name}</h3>
                                {item.combo_items?.length > 0 ? (
                                <p className="cart-item-meta">
                                    {item.combo_items.join(" • ")}
                                </p>
                                ) : (
                                <p className="cart-item-meta">
                                    {item.vendor_name} • {item.category_name}
                                </p>
                                )}
                                {(item.color_name || item.color_value) && (
                                    <div className="cart-item-color-pill">
                                        <span
                                        className="color-swatch"
                                        style={{
                                            backgroundColor: item.color_value || "#ccc"
                                        }}
                                        />
                                        <span className="color-text">
                                        {item.color_name} • {item.color_code}
                                        </span>
                                    </div>
                                )}
                                <p className="cart-item-price">${parseFloat(item.price).toFixed(2)} each</p>
                            </div>

                            <div className="cart-item-actions">
                                <div className="cart-item-total">
                                    ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                                </div>
                                
                                <div className="quantity-stepper">
                                    <button 
                                        className="quantity-btn"
                                        onClick={() => changeQuantity(item.sku, item.quantity - 1)}
                                        disabled={item.quantity <= 1}
                                    >
                                        −
                                    </button>
                                    <div className="quantity-value">{item.quantity}</div>
                                    <button 
                                        className="quantity-btn"
                                        onClick={() => changeQuantity(item.sku, item.quantity + 1)}
                                    >
                                        +
                                    </button>
                                </div>

                                <button 
                                    className="remove-item-btn"
                                    onClick={() => deleteItem(item.sku)}
                                    title="Remove item"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Order Summary */}
                <div className="order-summary-card">
                    <h2 className="summary-title">Order Summary</h2>

                    {/* Free Shipping Progress */}
                    {subtotal < freeShippingThreshold && (
                        <div className="shipping-progress">
                            <p className="shipping-progress-text">
                                ${amountToFreeShipping.toFixed(2)} away from free shipping!
                            </p>
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${shippingProgress}%` }}></div>
                            </div>
                        </div>
                    )}

                    <div className="summary-rows">
                        <div className="summary-row">
                            <span className="summary-label">Subtotal</span>
                            <span className="summary-value">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Shipping</span>
                            <span className="summary-value">
                                {subtotal >= freeShippingThreshold ? 'Free' : 'Calculated at checkout'}
                            </span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Taxes</span>
                            <span className="summary-value">Calculated at checkout</span>
                        </div>
                    </div>

                    <div className="summary-total">
                        <span className="total-label">Total</span>
                        <span className="total-value">${total.toFixed(2)}</span>
                    </div>

                    {/* Trust Signals */}
                    <div className="trust-signals-summary">
                        <div className="trust-item-summary">
                            <FaShieldAlt />
                            Secure checkout
                        </div>
                        <div className="trust-item-summary">
                            <FaUndo />
                            30-day returns
                        </div>
                        <div className="trust-item-summary">
                            <FaTruck />
                            Authentic brands
                        </div>
                    </div>

                    {/* CTAs */}
                    <div className="summary-actions">
                        <button 
                            className="checkout-btn-primary"
                            onClick={() => navigate('/checkout')}
                        >
                            Proceed to Checkout
                        </button>
                        <button 
                            className="continue-shopping-btn-secondary"
                            onClick={() => navigate('/products')}
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Sticky Checkout Bar */}
            <div className="mobile-checkout-bar">
                <div className="mobile-checkout-content">
                    <div className="mobile-total">
                        <span className="mobile-total-label">Total</span>
                        <span className="mobile-total-value">${total.toFixed(2)}</span>
                    </div>
                    <button 
                        className="mobile-checkout-btn"
                        onClick={() => navigate('/checkout')}
                    >
                        Checkout
                    </button>
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <div className="cart-toast">
                    {toast}
                </div>
            )}
        </div>
    )
}

export default ModernCart
